import { Browser } from "@capacitor/browser";
import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useAppState } from "contexts/AppContext";
import { loadPlatformDomainTokens } from "hooks/usePlatformDomainTokens";
import { useSwitchPlatform } from "hooks/useSwitchPlatform";
import { Models } from "api/types";

import { type ClassifiedLink, classifyLink, isRelativeAppPath } from "./classifyLink";
import type { LinkNavigationState } from "./linkNavigationState";

type OpenLinkOptions = {
  /** The screen the link was opened from; a dead link offers a way back to it. */
  returnTo?: string;
  /** Leave a web link unopened so the caller can show it first; the classification still resolves. */
  deferExternal?: boolean;
};

type Practice = Models<"GetMultiPlatformUserPlatformResult">;

/**
 * Opens a link from a message or push notification: in-app routes navigate (switching practice
 * first when the link targets another one the patient holds a token for), web links open in the
 * in-app browser, `tel:`/`mailto:` go to the operating system, and anything unsafe is dropped.
 *
 * Resolves with what the link was classified as.
 */
export const useOpenLink = () => {
  const navigate = useNavigate();
  const { platformId } = useAppState();
  const { switchToPlatform } = useSwitchPlatform();
  // The push context re-registers its native listeners whenever the handler built on this changes
  // identity, so the one value that changes on a practice switch is read through a ref.
  const currentPlatformId = useRef(platformId);
  currentPlatformId.current = platformId;

  const openLink = useCallback(
    async (link: string, options: OpenLinkOptions = {}): Promise<ClassifiedLink> => {
      // Absolute links are matched against the patient's practices, which relative paths never need.
      let practices: Practice[] | null = isRelativeAppPath(link.trim()) ? [] : null;
      practices ??= await loadPlatformDomainTokens();

      const classified = classifyLink(link, { practices });

      switch (classified.kind) {
        case "external":
          if (!options.deferExternal) {
            await Browser.open({ url: classified.url });
          }
          break;
        case "native":
          globalThis.location.href = classified.url;
          break;
        case "rejected":
          console.log("[RMD]: Ignoring link:", classified.reason);
          break;
        case "in-app": {
          if (classified.platformId && classified.platformId !== currentPlatformId.current) {
            const allPractices = practices.length ? practices : await loadPlatformDomainTokens();
            const target = allPractices.find(
              (practice) => practice.platformId === classified.platformId,
            );

            if (target) {
              await switchToPlatform(target, allPractices);
            } else {
              // No token for that practice, so we cannot act as them there. Opening the route on
              // the current practice is still better than dropping the link.
              console.log("[RMD]: No platform token for link target:", classified.platformId);
            }
          }

          if (options.returnTo) {
            navigate(classified.path, {
              state: { returnTo: options.returnTo } satisfies LinkNavigationState,
            });
          } else {
            navigate(classified.path);
          }
          break;
        }
      }

      return classified;
    },
    [navigate, switchToPlatform],
  );

  return { openLink };
};
